/**
 * VideoPanel: SubVideo 캐러셀의 개별 참여자 타일.
 * wehago-conference의 SubVideo/VideoPanel/VideoPanelPresenter.js 기반.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import VideoTile from '@/components/VideoTile/VideoTile';
import { useRemoteTracks, useLocalVideoTrack, useLocalScreenShareTrack } from '@/contexts/TrackContext';
import { useAppSelector } from '@/store/hooks';
import { shallowEqual } from 'react-redux';
import type { Participant } from '@/store/slices/participantsSlice';
import { isMasterUser } from '@/store/slices/masterSlice';
import { LANGUAGE_FLAG_MAP, type TranslationLanguage } from '@/store/slices/translationSlice';
import { DrawingGet } from '@/components/DrawingPanel/DrawingPanel';
import ContextMenu from '@/components/SVContent/Common/ContextMenu';
import WhiteboardContainer from '@/components/Whiteboard/WhiteboardContainer';
import Emoji from '@/components/SVContent/Common/Emoji';

interface VideoPanelProps {
  participant: Participant | 'local';
  localUserId: string;
  isMain?: boolean;
  onSelect?: (userId: string, showScreenShare?: boolean) => void;
  /** true: 하단 썸네일 캐러셀 (height:150 고정), false: 분할화면 그리드 (height:100%) */
  isThumbnail?: boolean;
  /** SubVideo 캐러셀 닫힘 상태 (0: 열림, 1/2: 닫힘) */
  hideLevel?: number;
  /** 강퇴 콜백 (마스터만) */
  onKickParticipant?: (userId: string) => void;
  expendArea ?: React.ReactNode; // SideScreen 메인 영상 확장/축소 버튼 영역 (SideScreen에서만 사용)
  /** true: 화면 공유 트랙을 표시 (카메라 대신) */
  showScreenShare?: boolean;
}

const getQualityLevel = (quality: number): 'good' | 'normal' | 'bad' => {
  if (quality >= 40) return 'good';
  if (quality >= 20) return 'normal';
  return 'bad';
};

const VideoPanel = React.memo<VideoPanelProps>(({
  participant,
  localUserId,
  isMain = false,
  onSelect,
  isThumbnail = false,
  hideLevel = 0,
  onKickParticipant,
  expendArea,
  showScreenShare = false,
}) => {
  const localData = useAppSelector((s) => ({
    connectionQuality: s.local.connectionQuality,
    userName: s.local.userName,
    nickname: s.local.nickname,
    userId: s.local.userId,
    avatarUrl: s.local.avatarUrl,
    wehagoId: s.local.wehagoId,
    audioMuted: s.local.audioMuted,
    videoMuted: s.local.videoMuted,
    hasVideoTrack: s.local.hasVideoTrack,
    jitsiId: s.local.jitsiId,
    isVideoReverse: s.local.isVideoReverse,
    selectedAvatar: s.local.selectedAvatar,
  }), shallowEqual);

  const isLocal = participant === 'local';
  const userId = isLocal ? localUserId : (participant as Participant).id;

  const activeSpeakers = useAppSelector((s) => s.audioIndicator.activeSpeakers);
  const remoteParticipant = useAppSelector((s) =>
    !isLocal ? s.participants.participants[userId] : undefined
  );
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const localScreenSharing = useAppSelector((s) => s.screenShare.isSharing);
  const drawing = useAppSelector((s) => s.drawing);
  const isTranslateEnabled = useAppSelector((s) => s.translation.isTranslateEnabled);
  const translationLanguage = useAppSelector((s) => s.translation.language);
  const translatingUsers = useAppSelector((s) => s.translation.translatingUsers);

  const isWhiteboard = !isLocal && !!(participant as Participant).isWhiteboard;

  const remoteTracks = useRemoteTracks(isLocal || isWhiteboard ? '' : userId);
  const localVideoTrack = useLocalVideoTrack();
  const localScreenShareTrack = useLocalScreenShareTrack();

  // showScreenShare가 true이면 화면 공유 트랙 표시, 아니면 카메라 트랙 표시
  const videoTrack = showScreenShare
    ? (isLocal ? localScreenShareTrack : remoteTracks.screenShare)
    : (isLocal ? localVideoTrack : remoteTracks.video);

  const connectionQuality = isLocal
    ? localData.connectionQuality
    : (remoteParticipant?.connectionQuality ?? 100);

  const handleClick = useCallback(() => {
    onSelect?.(userId, showScreenShare);
  }, [onSelect, userId, showScreenShare]);

  const qualityLevel = getQualityLevel(connectionQuality);
  const shouldShowNetworkStatus = qualityLevel !== 'good';


  // ─── 해상도 디버그 팝오버 (hooks는 early return 전에 선언해야 함) ──────
  const [showStats, setShowStats] = useState(false);
  const [videoRes, setVideoRes] = useState<{ w: number; h: number } | null>(null);
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startStats = useCallback(() => {
    setShowStats(true);
    // 타일 내 <video> 엘리먼트에서 실제 수신 해상도를 폴링
    const poll = () => {
      const container = document.getElementById(showScreenShare ? `video-item-${userId}-screen` : `video-item-${userId}`);
      const video = container?.querySelector('video');
      if (video && video.videoWidth > 0) {
        setVideoRes({ w: video.videoWidth, h: video.videoHeight });
      } else {
        setVideoRes(null);
      }
    };
    poll();
    statsTimerRef.current = setInterval(poll, 1000);
  }, [userId, showScreenShare]);

  const stopStats = useCallback(() => {
    setShowStats(false);
    if (statsTimerRef.current) {
      clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (statsTimerRef.current) clearInterval(statsTimerRef.current);
    };
  }, []);

  // 비디오 첫 프레임 수신 여부 (트랙은 있지만 아직 디코딩 안 된 경우 false)
  const [isVideoReady, setIsVideoReady] = useState(false);
  // 트랙이 바뀌면 ready 리셋
  useEffect(() => { setIsVideoReady(false); }, [videoTrack]);

  useEffect(() => {
    if (!shouldShowNetworkStatus) {
      stopStats();
    }
  }, [shouldShowNetworkStatus, stopStats]);

  // ─── 화이트보드 가짜 참여자 ───────────────────────
  if (isWhiteboard) {
    // isMain: DivisionScreen 메인 뷰 → 실제 ExcalidrawApp 렌더링
    if (isMain) {
      return (
        <div className="item guest whiteboard_main" style={{ width: '100%', height: '100%', position: 'relative' }}>
          <WhiteboardContainer isSubVideoMode={!isMain} />
        </div>
      );
    }
    // 썸네일/서브비디오 캐러셀: 간단 플레이스홀더
    return (
      <div className="item guest whiteboard_tile" onClick={handleClick} style={{ cursor: onSelect ? 'pointer' : 'default', background: '#fff', overflow: 'hidden' }}>
        <div className="movbx" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff' }}>
          {/* <span style={{ fontSize: 40 }}>📋</span> */}
          <WhiteboardContainer isSubVideoMode={!isMain} />
        </div>
        <div className="namebx active" style={{ zIndex: 2 }}>
          <strong className="name ellipsis" style={{maxWidth:"100%"}}>{(participant as Participant).displayName || '화이트보드'}</strong>
        </div>

        {expendArea}
      </div>
    );
  }

  // rtctest 기준: nickname 속성(API nickname)을 main 이름으로, displayName을 괄호 안에 표시
  const officialName = isLocal
    ? (localData.userName || localData.nickname || localData.userId)
    : (remoteParticipant?.displayName || remoteParticipant?.nickname || '');
  const nickname = isLocal ? localData.userName : (remoteParticipant?.displayName || '');
  const displayName = officialName;
  // 화면 공유 타일이면 이름을 "ㅇㅇㅇ의 화면 공유"로 변경
  const tileName = showScreenShare ? `${displayName}의 화면 공유` : displayName;
  const avatarUrl = isLocal ? localData.avatarUrl : remoteParticipant?.avatarUrl;
  const checkWehagoId = isLocal ? localData.wehagoId : (remoteParticipant?.wehagoId ?? '');
  const isMaster = isMasterUser(masterUsers, checkWehagoId);

  // 디버깅: 마스터 여부 확인
  if (!isLocal) {
    // console.log(`[VideoPanel] id=${userId} wehagoId=${checkWehagoId} isMaster=${isMaster} masterUsers=`, masterUsers);
  }

  const isAudioMuted = isLocal
    ? localData.audioMuted
    : (remoteParticipant?.tracks.audioMuted ?? false);

  const isScreenSharing = isLocal
    ? localScreenSharing
    : (remoteParticipant?.tracks.isScreenSharing ?? false);
  const isSpeaking = !isAudioMuted && activeSpeakers.includes(userId);

  // 사용자가 의도적으로 카메라를 끈 상태 (캐릭터 아바타 표시)
  const isVideoMuted = showScreenShare
    ? false
    : isLocal
      ? localData.videoMuted || !localData.hasVideoTrack
      : (remoteParticipant?.tracks.videoMuted ?? true);

  // 카메라는 켜져있지만 아직 비디오 트랙이 도착하지 않은 상태 (연결 중 — 엠티셋 표시)
  const isVideoConnecting = !isVideoMuted && !videoTrack;

  // 기존 isCameraOff: 의도적 카메라 OFF만 의미 (캐릭터 표시용)
  const isCameraOff = isVideoMuted;

  // 트랙 존재하지만 프레임 미도착 → 엠티셋 오버레이 필요
  const showEmptyOverlay = !isCameraOff && videoTrack && !isVideoReady;

  // 스케치 모드 확인
  const effectiveJitsiId = isLocal ? localData.jitsiId : (participant as Participant).id;
  const isVideoPanelSketchOwner = drawing.reduxIsSketchShare && drawing.reduxSketchOwner === effectiveJitsiId;

  // character 클래스: 카메라 OFF + 아바타 선택 시 적용 (CSS background-image 아바타 표시)
  // 마크업: item--guest item--guestCrt1 / item--guestCrt2 / item--guestCrt3
  const getCharacterClass = (customAvatar?: string) => {
    if (customAvatar === 'jangok') return 'item--guest item--guestCrt1';
    if (customAvatar === 'suzy') return 'item--guest item--guestCrt2';
    if (customAvatar === 'jessie') return 'item--guest item--guestCrt3';
    return '';
  };
  const selectedAvatar = isLocal ? localData.selectedAvatar : (participant as Participant).selectedAvatar;
  const characterClass = isCameraOff && !isVideoPanelSketchOwner ? getCharacterClass(selectedAvatar) : '';

  const tileId = showScreenShare ? `video-item-${userId}-screen` : `video-item-${userId}`;

  return (
    <div
      id={tileId}
      className={[
        'item',
        characterClass,
        isLocal ? 'is_me' : '',
        isMain ? 'main' : '',
        isSpeaking && !showScreenShare ? 'item--talk' : '',
        showScreenShare ? 'item--screenShare' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...(isThumbnail ? { height: hideLevel === 0 ? 150 : 'auto', cursor: onSelect ? 'pointer' : undefined } : { position: 'relative' }),
      }}
    >
      {/* 발화자 테두리 오버레이 */}
      {isSpeaking && !showScreenShare && (
        <div style={{ position: 'absolute', inset: 0, border: '2px solid #105aff', borderRadius: '8px', zIndex: 10, pointerEvents: 'none' }} />
      )}

      {/* 리액션 레이어 (마크업 기준 타일 최상단 배치) */}
      {/* <Emoji userId={isLocal ? local.wehagoId : (participant as Participant).wehagoId || userId} /> */}

      <button type="button" className={`movbx${(isVideoConnecting || showEmptyOverlay) ? ' empty' : ''}`} style={{ overflow: 'hidden' }} onClick={handleClick}>
        {!isCameraOff && videoTrack ? (
          <VideoTile
            track={videoTrack}
            isLocal={isLocal}
            isDesktop={showScreenShare}
            isReverse={isLocal ? localData.isVideoReverse : false}
            onReady={setIsVideoReady}
          />
        ) : null /* 카메라 OFF: character CSS, 연결 중: empty CSS */}

        {/* 스케치 오버레이 (캐러셀 미리보기) */}
        {isVideoPanelSketchOwner && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
            <DrawingGet
              sketchData={drawing.reduxSketchData ?? {}}
              sketchWidthLimit={drawing.reduxSketchWidthLimit}
              sketchHeightLimit={drawing.reduxSketchHeightLimit}
            />
          </div>
        )}

        {/* 클릭 캡쳐 레이어 */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 200 }} />
      </button>

      {/* 하단 이름/상태 바 — 마크업: movbx 다음에 namebx */}
      <div className="namebx">
        {!showScreenShare && ((isSpeaking) ? (
          <span className="micState active">
            <span className="wave">
              <span></span>
              <span></span>
            </span>
          </span>
        ) : (
          <button
            type="button"
            className={`btnMic${isAudioMuted ? ' off' : ''}`}
          >
            <span className="blind">마이크</span>
          </button>
        ))}
        <strong className="name ellipsis" title={tileName}>
          {tileName}
          {isLocal && !showScreenShare && '(나)'}
        </strong>
        {isTranslateEnabled && (() => {
          const lang = isLocal
            ? translationLanguage
            : translatingUsers[userId]?.language
              ?? (remoteParticipant?.nationality as TranslationLanguage | undefined);
          const flagSrc = lang && LANGUAGE_FLAG_MAP[lang as TranslationLanguage];
          return flagSrc ? <img className="namebx__flagIcon" src={flagSrc} alt="국기이미지" /> : null;
        })()}
      </div>

      {/* 상태 아이콘 — 마크업: namebx 다음에 statusBox */}
      <div className="statusBox" style={{ zIndex: 1001 }}>
        {isMaster && (
          <span className="statusBox_master">
            <span className="blind">마스터</span>
          </span>
        )}
        {shouldShowNetworkStatus && (
          <span
            className={`statusBox_network ${qualityLevel}`}
            onMouseEnter={startStats}
            onMouseLeave={stopStats}
            style={{ cursor: 'help' }}
          >
            <span className="blind">네트워크 상태 : {qualityLevel === 'normal' ? '보통' : '나쁨'}</span>
          </span>
        )}
        {showStats && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            background: 'rgba(0,0,0,0.85)',
            color: '#fff',
            fontSize: 11,
            lineHeight: '16px',
            padding: '6px 10px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            zIndex: 9999,
            pointerEvents: 'none',
            minWidth: 140,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            textAlign: 'left',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>{tileName}{isLocal ? ' (나)' : ''}</div>
            <div>수신 해상도: {videoRes ? `${videoRes.w}×${videoRes.h}` : (isCameraOff ? 'OFF' : '—')}</div>
            <div>연결 품질: {connectionQuality}%</div>
            <div>ID: {userId.slice(0, 12)}</div>
            {!isLocal && <div>sourceName: {remoteTracks.video ? '있음' : '없음'}</div>}
          </div>
        )}
      </div>

      {/* 우클릭 컨텍스트 메뉴 */}
      <ContextMenu
        userId={userId}
        containerId={tileId}
        onKickParticipant={onKickParticipant}
        isScreenShare={showScreenShare}
      />


      {expendArea}
    </div>
  );
});

export default VideoPanel;
